import { api, day } from "../../lib";
import { Loading } from "../../app/ui";
import { Checklist, Err, PartnerPage, SetupFooter, useAction, useSetup } from "./common";

/** The terms file is short markdown: paragraphs, numbered items and **bold**; the "# title" is the panel heading. */
function TermsText({ text }: { text: string }) {
  const blocks = text
    .split(/\n\s*\n/)
    .map((b) => b.replace(/\s*\n\s*/g, " ").trim())
    .filter((b) => b && !b.startsWith("# "));
  const bold = (s: string) =>
    s
      .split(/\*\*(.+?)\*\*/g)
      .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
  const items = (b: string) => b.split(/\s(?=\d+\.\s)/);
  return (
    <>
      {blocks.map((b, i) =>
        /^\d+\.\s/.test(b) ? (
          <ol key={i} className="stack">
            {items(b).map((it, j) => (
              <li key={j}>{bold(it.replace(/^\d+\.\s/, ""))}</li>
            ))}
          </ol>
        ) : (
          <p key={i} className="muted">
            {bold(b)}
          </p>
        ),
      )}
    </>
  );
}

export function ReviewPage() {
  return (
    <PartnerPage>
      {(pid, role) => <ReviewBody pid={pid} role={role} />}
    </PartnerPage>
  );
}

function ReviewBody({ pid, role }: { pid: string; role: string }) {
  const s = useSetup(pid);
  const terms = useAction((version: string) =>
    api("POST", `/api/partners/${pid}/terms/accept`, { version }),
  );
  const submit = useAction(() => api("POST", `/api/partners/${pid}/submit`));
  if (!s.data) return s.error ? <Err error={s.error} /> : <Loading />;
  const d = s.data;
  const open = d.checklist.filter((c) => c.due_now && !c.done);
  const t = d.terms;
  return (
    <div className="page narrow stack">
      <h1>Terms and review</h1>
      <section className="panel stack">
        <h2>Terms</h2>
        <div
          data-testid="partners-terms-text"
          className="stack"
          // a box with an edge, so text cut at the bottom reads as "scroll for more"
          style={{ maxHeight: "24rem", overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8, padding: "0.75rem 1rem" }}
        >
          <TermsText text={t.text} />
        </div>
        {t.accepted ? (
          <p data-testid="partners-terms-accepted">
            Accepted by {t.accepted_by_email ?? "the owner"}
            {t.accepted_at &&
              ` on ${day(t.accepted_at)}`}
            .
          </p>
        ) : role === "owner" ? (
          <button
            className="primary"
            data-testid="partners-terms-accept"
            disabled={terms.busy}
            onClick={() => terms.run(t.version)}
          >
            I accept these terms
          </button>
        ) : (
          <p className="muted">Your account owner accepts these.</p>
        )}
        <Err error={terms.error} />
      </section>
      {d.partner.status === "draft" && (
        <section className="panel stack">
          <Checklist items={d.checklist} />
          {role !== "viewer" && (
            <button
              className="primary"
              data-testid="partners-submit"
              disabled={submit.busy || open.length > 0}
              onClick={() => submit.run(undefined)}
            >
              Send for review
            </button>
          )}
        </section>
      )}
      {d.partner.status === "pending_review" && (
        <p data-testid="partners-submitted">Sent. We'll switch you on soon.</p>
      )}
      {d.partner.status === "live" && (
        <p data-testid="partners-live">You're live.</p>
      )}
      <Err error={submit.error} />
      <SetupFooter pid={pid} step="terms" />
    </div>
  );
}
